/**
 * The chosen village plan, as the plan page and Home read it (village rebuild M2, 21 Oct 2026 — the
 * Support at home pattern, sahChosenPlan.ts).
 *
 * ONE PLAN PER UNIT (owner, 20–21 Oct 2026). A family may model more than one village or unit; each keeps
 * one saved plan, keyed by the village's name and the entry payment, and one of them is the chosen plan.
 * Choosing another plan for the same unit replaces that unit's plan; choosing for another unit keeps the
 * first one saved and marks the new one chosen.
 *
 * The card the family chose is stored on the record — its figures, staying put's at the time, the plan in
 * words, the contract — so the plan page shows exactly what was chosen, with no re-run in between.
 */
import { withChosenPlan, type PlanRecordLike } from '../../strategy/chosenPlan.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';

export interface RvFigures { legacy: number; pension: number; cashflow: number }

export interface RvPlanCardRecord {
    plan: RvFigures;
    baseline: RvFigures | null;
    words: { title: string; sub: string };
    village: { name: string; entryFee: number; annualOngoing: number; fees: number; feesFrequency: string; departureFee: number; backAfter: number };
    horizonYears: number;
    riskKinds: RiskKind[];
    /** The plan's net rent a year from a kept, rented home (0 when the home is sold or kept empty). */
    rentNet: number;
    /** 'engine' — one of the tested plans; 'manual' — built by hand. */
    origin: 'engine' | 'manual';
    /**
     * A plan with a guaranteed income: what the family receives if death comes early, frozen when the plan was
     * chosen, so the plan page shows the same table as the card (guaranteed income review E4/M4, 21 Oct 2026).
     */
    family?: RvFamilyRecord | null;
    /**
     * The card's "When the money runs out" sentence (runwayCardLine) and what "What could go wrong" says about
     * this plan in particular, frozen when chosen like the rest of the card (22 Oct 2026). Older records have
     * neither: the plan page then leaves the line out, and the risks fall back to what the saved inputs carry.
     */
    runLine?: string | null;
    riskSpecifics?: { horizonWarning: string | null; stampDuty: number | null } | null;
}

export interface RvFamilyRecord {
    personName: string;
    amount: number;
    kind: 'fullBack' | 'moneyBack' | 'incomeOnly';
    incomeAYear: number | null;
    compareNote: string | null;
    rows: Array<{ label: string; fromAnnuity: number; withPlan: number | null; without: number | null }>;
}

export interface ChosenRvPlan extends PlanRecordLike {
    id: string;
    createdAt?: string;
    soaSnapshot?: unknown;
    inputs?: { card?: RvPlanCardRecord; unitKey?: string; engineStrategyId?: string | null; [key: string]: unknown } | null;
}

/**
 * The tested plan's id behind THE chosen plan — read off that plan, never off any other record that also says
 * chosen (25 Oct 2026). The page used to find "a chosen tested plan" on its own; a file with an older tested
 * plan still flagged chosen then wrote that plan's runway onto a hand-built plan's card ("borrows against the
 * home" on a plan that sells it). A hand-built plan has no tested twin, so it has no id.
 */
export function rvChosenEngineIdOf(plan: ChosenRvPlan | null | undefined): string | null {
    if (!plan || (plan as { source?: string }).source !== 'suggestion_engine') return null;
    const id = plan.inputs?.engineStrategyId;
    return typeof id === 'string' ? id : null;
}

/**
 * When a hand-built plan could first save its own runway line (19ff602, 22 Oct 2026 18:38 AEDT). A hand-built
 * card chosen before then can only have had its line written by the catch-up above, from another plan's rows —
 * so it is not shown.
 */
export const RV_MANUAL_RUNLINE_FROM = '2026-10-22T07:38:26Z';

/** The chosen card's "When the money runs out" line, or null where it cannot be this plan's own. */
export function rvCardRunLine(plan: ChosenRvPlan | null | undefined): string | null {
    const card = plan?.inputs?.card;
    if (!card?.runLine) return null;
    if (card.origin === 'manual') {
        const chosenAt = Date.parse(String(plan?.createdAt || ''));
        if (!Number.isFinite(chosenAt) || chosenAt < Date.parse(RV_MANUAL_RUNLINE_FROM)) return null;
    }
    return card.runLine;
}

/** "banksia grove|650000" — the unit a plan belongs to. */
export function rvUnitKey(name: string | null | undefined, entryFee: number): string {
    return `${String(name || '').trim().toLowerCase()}|${Math.round(Number(entryFee) || 0)}`;
}

/** Choosing a plan: the unit's earlier plan is replaced, the other units' plans are kept, the new one is chosen. */
export function withChosenRvPlan<T extends PlanRecordLike>(prev: readonly T[] | null | undefined, record: T): T[] {
    const key = (record.inputs as { unitKey?: string } | null | undefined)?.unitKey;
    const others = (prev || []).filter((p) => !(p.type === 'retirement_village' && key != null && (p.inputs as { unitKey?: string } | null | undefined)?.unitKey === key));
    return withChosenPlan(others, record);
}

/** The chosen village plan on the file, if one is chosen and carries its card. */
export function chosenRvPlanOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenRvPlan | null {
    const hit = (list || []).find((s) => s?.type === 'retirement_village' && s.chosen === true && (s.inputs as { card?: unknown } | undefined)?.card);
    return (hit as ChosenRvPlan | undefined) ?? null;
}

/** Every unit's saved plan that carries its card, the chosen one first. */
export function rvUnitPlansOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenRvPlan[] {
    const withCard = (list || []).filter((s) => s?.type === 'retirement_village' && (s.inputs as { card?: unknown } | undefined)?.card) as ChosenRvPlan[];
    return [...withCard].sort((a, b) => Number(b.chosen === true) - Number(a.chosen === true));
}

/** Session key: Home's "Start a new model" — open on an empty step 1 and ask step 3's questions again (one-shot). */
export const RV_START_FRESH_KEY = 'careCompanionRvStartFresh';
/** Session key: Home's "Model another village or unit" — open step 1 with everything carried over (one-shot). */
export const RV_ANOTHER_UNIT_KEY = 'careCompanionRvAnotherUnit';

export function takeSessionFlag(key: string): boolean {
    try {
        const v = window.sessionStorage.getItem(key);
        if (v) window.sessionStorage.removeItem(key);
        return !!v;
    } catch {
        return false;
    }
}
